import { readMotorQuery } from './mxpro-garage-parts-search.js';

const motorTag = /^(?:mxpro-ymm-[a-f0-9]+|mxp-fit-[a-z0-9-]+|[a-z0-9-]+-esi\d+)$/;
const route = /^\/collections\/(?:all\/)?((?:mxpro-ymm-[a-f0-9]+|mxp-fit-[a-z0-9-]+|[a-z0-9-]+-esi\d+))\/?$/;

export function clearMotorDestination(value, { root = '/', all = '/collections/all', road = '/collections/road', world = 'cross' } = {}) {
  const page = new URL(value);
  const prefix = root.replace(/\/$/, '');
  const path = page.pathname.startsWith(prefix + '/') ? page.pathname.slice(prefix.length) : page.pathname;
  const query = readMotorQuery(page.searchParams.get('q'));
  const routeMotor = route.test(path);
  let removedTag = false;
  const target = routeMotor ? new URL(world === 'road' ? road : all, page.origin) : new URL(page.href);
  if (target.origin !== page.origin) return null;
  target.search = page.search;
  target.hash = '';
  for (const key of [...new Set(target.searchParams.keys())]) {
    if (!/^filter\.p\.tag$/i.test(key)) continue;
    const values = target.searchParams.getAll(key);
    const remaining = values.filter(v => !motorTag.test(v));
    if (remaining.length !== values.length) {
      removedTag = true;
      target.searchParams.delete(key);
      remaining.forEach(v => target.searchParams.append(key, v));
    }
  }
  if (!routeMotor && !query && !removedTag) return null;
  if (query) target.searchParams.set('q', query.term);
  target.searchParams.delete('page');
  if (world === 'road') target.searchParams.set('view', 'mxpro-road');
  return target.href;
}

export function validGarageMotor(motor, world) {
  return motor?.segment === world && motor.classificationRevision === '20261018review1' &&
    typeof motor.make === 'string' && typeof motor.model === 'string' &&
    Number.isInteger(motor.year) && motor.year >= 1900 && motor.year <= 2030;
}

if (typeof customElements !== 'undefined' && !customElements.get('mxpro-garage-manager')) {
  customElements.define('mxpro-garage-manager', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.world = this.dataset.world;
      if (!['cross', 'road'].includes(this.world)) return;
      this.dialog = this.querySelector('dialog');
      this.copy = JSON.parse(this.querySelector('[data-garage-copy]').textContent);
      this.key = 'mxpro-next-garage-' + this.world;
      this.header = document.querySelector('.c-header__icon--partfinder');
      this.status = this.querySelector('[data-garage-status]');
      const headerInner = this.header?.closest('.c-header__inner');
      if (headerInner) {
        this.mobile = document.createElement('button');
        this.mobile.type = 'button';
        this.mobile.className = 'mxpro-garage-mobile';
        this.mobile.textContent = this.copy.title;
        this.mobile.setAttribute('aria-haspopup', 'dialog');
        this.mobile.setAttribute('aria-controls', 'mxpro-garage-dialog');
        this.mobile.addEventListener('click', () => this.open(this.mobile));
        headerInner.append(this.mobile);
      }
      if (this.header) {
        this.header.setAttribute('aria-haspopup', 'dialog');
        this.header.setAttribute('aria-controls', 'mxpro-garage-dialog');
        this.header.setAttribute('role', 'button');
        this.header.addEventListener('click', event => { event.preventDefault(); this.open(this.header); });
        this.header.addEventListener('keydown', event => { if (event.key === ' ') { event.preventDefault(); this.open(this.header); } });
      }
      this.querySelector('[data-garage-close]').addEventListener('click', () => this.dialog.close());
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const box = this.dialog.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) this.dialog.close();
      });
      this.dialog.addEventListener('close', () => this.opener?.isConnected && this.opener.focus());
      document.addEventListener('click', event => {
        const clear = event.target.closest('[data-garage-clear], [data-vehicle-clear]');
        if (clear) this.clear();
      });
      document.addEventListener('mxpro:vehicle-change', () => this.update());
      window.addEventListener('storage', event => { if (!event.key || event.key === this.key) this.update(); });
      window.addEventListener('pageshow', () => this.update());
      this.update();
    }
    saved() {
      try { const motor = JSON.parse(localStorage.getItem(this.key)); return validGarageMotor(motor, this.world) ? motor : null; } catch { return null; }
    }
    update() {
      const saved = this.saved();
      const banner = document.getElementById('mxpro-selected-motor');
      const routeName = banner && !banner.hidden ? banner.querySelector('[data-mxpro-selected-motor-name]')?.textContent?.trim() : '';
      const name = saved ? `${saved.make} ${saved.model} (${saved.year})` : routeName;
      this.querySelector('[data-garage-selected]').hidden = !name;
      this.querySelector('[data-garage-empty]').hidden = !!name;
      this.querySelector('[data-garage-motor]').textContent = name || '';
      if (this.header) {
        this.header.querySelector('.c-header__icon--text').textContent = this.copy.title;
        this.header.setAttribute('aria-label', name ? `${this.copy.title}: ${name}` : this.copy.title);
      }
      if (this.mobile) this.mobile.setAttribute('aria-label', name ? `${this.copy.title}: ${name}` : this.copy.title);
      const page = new URL(location.href);
      for (const link of this.querySelectorAll('[data-garage-change]')) {
        const target = new URL(link.href, page);
        if (page.searchParams.has('preview_theme_id')) target.searchParams.set('preview_theme_id', page.searchParams.get('preview_theme_id'));
        if (this.world === 'road') target.searchParams.set('view', 'mxpro-road');
        link.href = target.href;
      }
    }
    open(opener) {
      this.opener = opener;
      this.status.textContent = '';
      this.update();
      if (!this.dialog.open) this.dialog.showModal();
    }
    clear() {
      const saved = this.saved();
      try {
        localStorage.removeItem(this.key);
      } catch {
        this.status.textContent = this.copy.error;
        return;
      }
      try {
        const legacy = JSON.parse(sessionStorage.getItem('mxpro-ymm-selection-v1'));
        if (legacy && saved && legacy.make === saved.make && legacy.model === saved.model && Number(legacy.year) === saved.year) sessionStorage.removeItem('mxpro-ymm-selection-v1');
      } catch {}
      const destination = clearMotorDestination(location.href, this.dataset);
      document.dispatchEvent(new Event('mxpro:vehicle-change'));
      for (const finder of document.querySelectorAll('mxpro-vehicle-finder')) {
        if (finder.dataset.world !== this.world || !finder.rows) continue;
        finder.make.value = '';
        finder.models();
        finder.querySelector('[data-vehicle-selected]').hidden = true;
        const query = finder.querySelector('[data-vehicle-query]');
        if (query) query.value = '';
        finder.querySelector('[data-vehicle-suggestions]')?.replaceChildren();
      }
      if (destination) { location.replace(destination); return; }
      const banner = document.getElementById('mxpro-selected-motor');
      if (banner) banner.hidden = true;
      this.update();
      this.status.textContent = this.copy.cleared;
      if (!this.dialog.open) (this.mobile?.getClientRects().length ? this.mobile : this.header)?.focus();
    }
  });
}
